// CHANGELOG.md, read for the Changelog page. The file lives at the repo root and is built into
// the client (the Dockerfile copies it in), so the page works offline and always describes the
// version this phone is running. The page skips what it cannot read; the tests do not.
import source from "../../../CHANGELOG.md?raw";

export interface ChangeGroup {
  /** Added, Changed, Fixed, ... */
  title: string;
  /** one entry each; a `code` span is the only markup */
  items: string[];
}

export interface Release {
  /** null for "## Unreleased" */
  version: string | null;
  /** YYYY-MM-DD; null for Unreleased */
  date: string | null;
  groups: ChangeGroup[];
}

const RELEASE = /^## (\d+\.\d+\.\d+) - (\d{4}-\d{2}-\d{2})$/;

export function parseChangelog(text: string): { releases: Release[]; problems: string[] } {
  const releases: Release[] = [];
  const problems: string[] = [];
  let release: Release | null = null;
  let items: string[] | null = null;
  for (const [i, raw] of text.split(/\r?\n/).entries()) {
    const line = raw.trimEnd();
    const m = RELEASE.exec(line);
    if (line === "## Unreleased" || m) {
      release = { version: m?.[1] ?? null, date: m?.[2] ?? null, groups: [] };
      releases.push(release);
      items = null;
    } else if (line.startsWith("## ")) {
      // a heading the page cannot read: leave out everything under it
      release = null;
      problems.push(`line ${i + 1}: ${line}`);
    } else if (!release) {
      // the introduction, before the first release
    } else if (line.startsWith("### ")) {
      items = [];
      release.groups.push({ title: line.slice(4), items });
    } else if (line.startsWith("- ") && items) {
      items.push(line.slice(2));
    } else if (/^\s+\S/.test(line) && items?.length) {
      items[items.length - 1] += ` ${line.trim()}`; // an entry wrapped onto the next line
    } else if (line !== "") {
      problems.push(`line ${i + 1}: ${line}`);
    }
  }
  for (const r of releases) {
    for (const item of r.groups.flatMap((g) => g.items)) {
      if (item.split("`").length % 2 === 0) problems.push(`unclosed \` in: ${item}`);
    }
  }
  return { releases, problems };
}

/** What the Changelog page lists: every release with something in it, newest first. */
export const RELEASES = parseChangelog(source).releases.filter((r) => r.groups.length > 0);
